import { z } from "zod";
import type { OrderItemDto } from "./order";

export const ImportSourceType = z.enum(["pdf"]);
export const ImportParserId = z.enum([
  "cqree_v1",
  "aoi_tei_v1",
  "delicious_take_v1",
  "star_festival_delivery_v1",
  "sfp_simple_v1",
  "obento_deli_v1",
  "catering_selection_v1",
  "generic_pdf",
  "unknown",
]);
export const ImportParseMethod = z.enum(["local", "gemini", "manual"]);

/** 取引先マスタの既定値で補完しうるプレビュー項目 (Issue #613)。 */
export const ImportMasterFilledField = z.enum(["pickup_address"]);

/**
 * 取込時に許容幅で補完した時刻の項目 (#657)。値は**受注フォームのフィールド名**に合わせる
 * (preview 側の `delivery_time_from/to` ではなく `delivery_time_start/end`)。
 * admin は「PDF反映」と区別して「自動補完」バッジを出すためにこれを見る。
 */
export const ImportTimeBackfilledField = z.enum([
  "delivery_time_start",
  "delivery_time_end",
  "pickup_time_start",
  "pickup_time_end",
]);

export const ParsedOrderItemPreview = z.object({
  product_name: z.string(),
  quantity: z.number().int().min(1),
  unit_price: z.number().int().nullable(),
  /** 商品重量 (kg). 解析で得られなければ null. optimizer の積載判定で利用する. */
  weight_kg: z.number().nullable().default(null),
});

/**
 * PDF を解析して admin に返す受注プレビュー.
 *
 * - 解析で得られなかった項目は null / 空文字で返す.
 * - admin 側で確認・修正してから通常の `order.create` を叩いて確定する.
 */
export const ParsedOrderPreview = z.object({
  source_type: ImportSourceType,
  source_file_name: z.string(),
  parser_id: ImportParserId,
  parse_method: ImportParseMethod,
  /** 0.0 (解析失敗) 〜 1.0 (全項目抽出成功). 0.8 以上を admin で自動確定候補とする. */
  confidence: z.number().min(0).max(1),
  /** 信頼度算出の根拠 (UI で表示可). */
  confidence_reasons: z.array(z.string()),
  /** 解析対象の生テキスト (PDF→テキスト). admin の検証用. */
  raw_text: z.string(),

  order_number: z.string().nullable(),
  payment_number: z.string().nullable(),
  delivery_date: z.string(), // YYYY-MM-DD (解析失敗時は空文字)
  delivery_time_from: z.string().nullable(),
  delivery_time_to: z.string().nullable(),
  delivery_address: z.string(),
  facility_name: z.string().nullable(),
  handover_location: z.string().nullable(),
  arrival_notes: z.string().nullable(),

  delivery_name: z.string().nullable(),
  delivery_name_kana: z.string().nullable(),
  delivery_phone: z.string().nullable(),
  delivery_email: z.string().nullable(),
  delivery_company: z.string().nullable(),
  delivery_branch: z.string().nullable(),

  payment_method: z.string().nullable(),
  total_amount: z.number().int().nullable(),

  items: z.array(ParsedOrderItemPreview),

  pickup_store_name: z.string().nullable(),
  pickup_address: z.string().nullable(),
  /**
   * 集荷先の緯度・経度 (Issue #613)。
   * 取引先マスタの集荷住所で補完したときだけ、そのマスタ座標を併せて返す。
   * PDF 解析自体は座標を持たないため、通常は null (admin 側で住所からジオコーディングする)。
   */
  pickup_lat: z.number().nullable().default(null),
  pickup_lng: z.number().nullable().default(null),
  pickup_phone: z.string().nullable(),
  /**
   * 集荷の指定時刻 (#657)。注文書から取れなければ null。
   * 片側だけ抽出できた場合は取込時に許容幅で補完される (配達側と同じ扱い)。
   */
  pickup_time_start: z.string().nullable().default(null),
  pickup_time_end: z.string().nullable().default(null),
  packaging: z.string().nullable(),
  store_message: z.string().nullable(),
  delivery_request: z.string().nullable(),

  remarks: z.string().nullable(),

  /**
   * R2 に永続化された uploaded_files の public_id.
   * null の場合は storage へ保存していない (テスト / 解析不能ケース).
   * admin UI から order.create 呼出時にそのまま渡すと、uploaded_files.status='processed'
   * へ遷移し order とリンクする.
   */
  uploaded_file_public_id: z.string().nullable(),

  /**
   * マスタ自動マッチングの推奨候補. admin UI で確認 → 確定時に
   * `order.create` payload の `pickup_spot_public_id` / `delivery_spot_public_id` / `client_id` へコピー.
   * score は 0.6 以上のみ. マッチなしは null.
   */
  suggested_pickup_spot: z
    .object({ public_id: z.string(), score: z.number() })
    .nullable()
    .default(null),
  suggested_delivery_spot: z
    .object({ public_id: z.string(), score: z.number() })
    .nullable()
    .default(null),
  suggested_client: z.object({ public_id: z.string(), score: z.number() }).nullable().default(null),

  /**
   * 注文書から取れず取引先マスタの既定値で補完したフィールド (Issue #613).
   * admin のプレビューで「取引先マスタから補完した値」であることを提示するために使う.
   * 注文書に記載があればそちらが正のため、補完は解析結果が空のときだけ行う.
   */
  master_filled_fields: z.array(ImportMasterFilledField).default([]),
  /**
   * 取込時に許容幅で補完した時刻の項目 (#657)。注文書に書かれていた値と区別するために使う。
   */
  time_backfilled_fields: z.array(ImportTimeBackfilledField).default([]),
});

export const ImportOrderOutput = z.object({
  /** 1 PDF ファイルにつき 1 件以上の取込候補. */
  previews: z.array(ParsedOrderPreview),
});

export type ImportSourceTypeDto = z.infer<typeof ImportSourceType>;
export type ImportParserIdDto = z.infer<typeof ImportParserId>;
export type ImportParseMethodDto = z.infer<typeof ImportParseMethod>;
export type ImportMasterFilledFieldDto = z.infer<typeof ImportMasterFilledField>;
export type ImportTimeBackfilledFieldDto = z.infer<typeof ImportTimeBackfilledField>;
export type ParsedOrderItemPreviewDto = z.infer<typeof ParsedOrderItemPreview>;
export type ParsedOrderPreviewDto = z.infer<typeof ParsedOrderPreview>;
export type ImportOrderOutputDto = z.infer<typeof ImportOrderOutput>;

/**
 * 取込プレビュー (フラット・旧命名) を Order DTO のフィールド名へ橋渡しする単一源泉。
 *
 * ParsedOrderPreview は V3 命名統一前の旧名 (facility_name / delivery_name 等) を保持するため、
 * 確定時に Order DTO 命名 (delivery_building / delivery_person_name 等) へ写し替える必要がある。
 * この対応を API (buildImportPayload) と admin (buildCreateImportPayload/buildUpdateImportPayload)
 * の双方が import して使い、命名ドリフトに伴う二重マッピングを解消する。
 *
 * マスタ参照 (client_id / pickup_spot_public_id / delivery_spot_public_id)・座標・モード別
 * (create=undefined 除外 / update=null 維持) の取り扱いは呼び出し側の責務とし、ここでは
 * preview のフラット値 → Order 命名のフィールド対応のみを担う。weight_kg もここで明細へ伝播する。
 */
export function mapPreviewItemToOrderItem(item: ParsedOrderItemPreviewDto): OrderItemDto {
  return {
    product_name: item.product_name,
    quantity: item.quantity,
    unit_price: item.unit_price,
    weight_kg: item.weight_kg,
  };
}

/** preview → Order DTO 命名のフィールド対応 (値はそのまま素通し、命名のみ統一)。 */
export function mapPreviewToOrderFields(preview: ParsedOrderPreviewDto): PreviewMappedOrderFields {
  return {
    order_number: preview.order_number,
    payment_number: preview.payment_number,
    payment_type: preview.payment_method,
    delivery_address: preview.delivery_address,
    delivery_date: preview.delivery_date,
    delivery_time_start: preview.delivery_time_from,
    delivery_time_end: preview.delivery_time_to,
    delivery_building: preview.facility_name,
    handover_location: preview.handover_location,
    arrival_instructions: preview.arrival_notes,
    delivery_company: preview.delivery_company,
    delivery_branch: preview.delivery_branch,
    delivery_person_name: preview.delivery_name,
    delivery_person_name_kana: preview.delivery_name_kana,
    delivery_email: preview.delivery_email,
    delivery_phone: preview.delivery_phone,
    store_name: preview.pickup_store_name,
    store_address: preview.pickup_address,
    store_phone: preview.pickup_phone,
    pickup_time_start: preview.pickup_time_start,
    pickup_time_end: preview.pickup_time_end,
    delivery_packaging: preview.packaging,
    store_message: preview.store_message,
    delivery_notes: preview.delivery_request,
    remarks: preview.remarks,
    uploaded_file_public_id: preview.uploaded_file_public_id,
    items: preview.items.map(mapPreviewItemToOrderItem),
  };
}

/** mapPreviewToOrderFields の戻り値型 (Order DTO 命名のフラットなフィールド群)。 */
export interface PreviewMappedOrderFields {
  order_number: string | null;
  payment_number: string | null;
  payment_type: string | null;
  delivery_address: string;
  delivery_date: string;
  delivery_time_start: string | null;
  delivery_time_end: string | null;
  delivery_building: string | null;
  handover_location: string | null;
  arrival_instructions: string | null;
  delivery_company: string | null;
  delivery_branch: string | null;
  delivery_person_name: string | null;
  delivery_person_name_kana: string | null;
  delivery_email: string | null;
  delivery_phone: string | null;
  store_name: string | null;
  store_address: string | null;
  store_phone: string | null;
  pickup_time_start: string | null;
  pickup_time_end: string | null;
  delivery_packaging: string | null;
  store_message: string | null;
  delivery_notes: string | null;
  remarks: string | null;
  uploaded_file_public_id: string | null;
  items: OrderItemDto[];
}
